import { Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LoginScreen } from "./screens/LoginScreen.js";
import { OpsHubScreen } from "./screens/OpsHubScreen.js";
import { AdminLayout } from "./components/AdminLayout.js";
import { AuthGuard } from "./components/AuthGuard.js";
import { RequireModuleAccess } from "./components/RequireModuleAccess.js";
import { SettingsLanding } from "./modules/settings/SettingsLanding.js";
import { ReceptionLanding } from "./modules/reception/ReceptionLanding.js";
import { CashSubscreen } from "./modules/reception/CashSubscreen.js";
import { CouriersSubscreen } from "./modules/reception/CouriersSubscreen.js";
import { DeliverySubscreen } from "./modules/reception/DeliverySubscreen.js";
import { PickupsSubscreen } from "./modules/reception/PickupsSubscreen.js";
import { OrdersLanding } from "./modules/orders/OrdersLanding.js";
import { AllOrdersSubscreen } from "./modules/orders/AllOrdersSubscreen.js";
import { AccountsLanding } from "./modules/accounts/AccountsLanding.js";
import { ClientsLanding } from "./modules/clients/ClientsLanding.js";
import { PaymentsVerificationLanding } from "./modules/payments-verification/PaymentsVerificationLanding.js";
import { KbLanding } from "./modules/kb/KbLanding.js";
import { TasksLanding } from "./modules/tasks/TasksLanding.js";
import { WarehouseLanding } from "./modules/warehouse/WarehouseLanding.js";
import { IntegrationsLanding } from "./modules/integrations/IntegrationsLanding.js";
// Ozon/WB админ-экраны (intake-review, intake-tbilisi, sorting-ru, shipments,
// QR-очередь и deprecated-сабскрины заказов) удалены 2026-06-12 — модуль
// заархивирован в ветке archive/ozonwb-flow (тег pre-ozonwb-extract).
import { RoleProvider } from "./lib/role.tsx";
import type { Role } from "./lib/role.ts";
import { api } from "./lib/api.js";

interface AppProps {
  testRole?: Role;
}

export default function App({ testRole }: AppProps) {
  const { data: meData } = useQuery({
    queryKey: ["admin-me"],
    queryFn: () =>
      api<{ chatId: string; username: string | null; role: string }>(
        "/admin/auth/me",
      ),
    enabled: testRole === undefined,
    retry: false,
  });

  // API returns AdminRole strings that match frontend Role values directly.
  const apiRole = meData?.role;
  const resolvedRole = testRole ?? (apiRole as Role | undefined);

  return (
    <RoleProvider role={resolvedRole}>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route element={<AuthGuard />}>
          <Route element={<AdminLayout />}>
            <Route path="/" element={<OpsHubScreen />} />
            <Route path="/m/reception/*" element={<RequireModuleAccess slug="reception" />}>
              <Route element={<ReceptionLanding />}>
                <Route path="delivery" element={<DeliverySubscreen />} />
                <Route path="cash" element={<CashSubscreen />} />
                <Route path="pickups" element={<PickupsSubscreen />} />
                <Route path="couriers" element={<CouriersSubscreen />} />
                <Route path="kb-shortcut" element={<Navigate to="/m/kb" replace />} />
              </Route>
            </Route>
            <Route path="/m/orders/*" element={<RequireModuleAccess slug="orders" />}>
              <Route element={<OrdersLanding />}>
                <Route path="all" element={<AllOrdersSubscreen />} />
              </Route>
            </Route>
            <Route path="/m/accounts/*" element={<RequireModuleAccess slug="accounts" />}>
              <Route index element={<AccountsLanding />} />
              <Route path="*" element={<AccountsLanding />} />
            </Route>
            <Route path="/m/clients/*" element={<RequireModuleAccess slug="clients" />}>
              <Route index element={<ClientsLanding />} />
              <Route path="*" element={<ClientsLanding />} />
            </Route>
            <Route path="/m/payments/*" element={<RequireModuleAccess slug="payments" />}>
              <Route index element={<PaymentsVerificationLanding />} />
              <Route path="*" element={<PaymentsVerificationLanding />} />
            </Route>
            <Route path="/m/kb/*" element={<RequireModuleAccess slug="kb" />}>
              <Route index element={<KbLanding />} />
              <Route path="*" element={<KbLanding />} />
            </Route>
            <Route path="/m/tasks/*" element={<RequireModuleAccess slug="tasks" />}>
              <Route index element={<TasksLanding />} />
              <Route path="*" element={<TasksLanding />} />
            </Route>
            <Route path="/m/warehouse/*" element={<RequireModuleAccess slug="warehouse" />}>
              <Route index element={<WarehouseLanding />} />
              <Route path="*" element={<WarehouseLanding />} />
            </Route>
            {/* Интеграции (outbox-admin-visibility, волна 4) — НЕ модуль из
                MODULE_ACCESS; страница сама гейтится integrations:manage. */}
            <Route path="/m/integrations" element={<IntegrationsLanding />} />
            <Route path="/m/:slug/*" element={<RequireModuleAccess />} />
            <Route path="/settings/*" element={<SettingsLanding />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RoleProvider>
  );
}
